import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
  useAlert,
} from '@ads/components-react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DeleteStateModal,
  STATE_PAGE_SIZE,
  StateFormModal,
  StateSearchBar,
  StateTable,
  useDeleteState,
  useStateFilters,
  useStateList,
} from '@/features/location';
import type { StateViewModel } from '@/shared/api/generated';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';

export const Route = createFileRoute('/_authenticated/location/states/')({
  component: StateListPage,
});

/** Página de listagem de Estados (área de Localização). */
export function StateListPage() {
  const { t } = useTranslation('location');
  const { alert } = useAlert();

  const { states, isLoading, isError, refetch } = useStateList();
  const { filters, updateFilters } = useStateFilters();
  const deleteState = useDeleteState();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<StateViewModel | null>(null);
  const [deleting, setDeleting] = useState<StateViewModel | null>(null);

  // TODO: busca client-side — o endpoint `GET /api/v1/location/states` ainda não
  // aceita parâmetro de busca. Quando o backend implementar (ex.: `?search=`),
  // enviar `filters.search` via `useStateList` e remover a filtragem abaixo.
  const searched = useMemo(() => {
    const query = filters.search.trim().toLowerCase();
    if (!query) return states;
    return states.filter(
      state =>
        (state.name ?? '').toLowerCase().includes(query) ||
        (state.code ?? '').toLowerCase().includes(query) ||
        (state.abbreviation ?? '').toLowerCase().includes(query),
    );
  }, [states, filters.search]);

  // TODO: paginação client-side — o endpoint retorna a lista completa. Quando o
  // backend paginar (page/pageSize/total), passar esses parâmetros para
  // `useStateList`, usar os totais do envelope e remover o fatiamento. Ver `STATE_PAGE_SIZE`.
  const totalPages = Math.max(1, Math.ceil(searched.length / STATE_PAGE_SIZE));
  const currentPage = Math.min(filters.page, totalPages);
  const pageData = useMemo(() => {
    const start = (currentPage - 1) * STATE_PAGE_SIZE;
    return searched.slice(start, start + STATE_PAGE_SIZE);
  }, [searched, currentPage]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (state: StateViewModel) => {
    setEditing(state);
    setFormOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (deleting?.id == null) return;
    deleteState.mutate(
      { id: deleting.id },
      {
        onSuccess: () => alert({ alertType: 'success', title: t('states.toast.deleteSuccess') }),
        onError: () => alert({ alertType: 'warning', title: t('states.toast.deleteError') }),
        onSettled: () => setDeleting(null),
      },
    );
  };

  const addButton = (
    <Button hierarchy="primary" size="lg" leadingIcon={PlusIcon} onClick={openCreate}>
      {t('states.actions.add')}
    </Button>
  );

  return (
    <div className="flex flex-col gap-xxs">
      <PageHeader
        title={t('states.title')}
        subtitle={t('states.subtitle', { count: states.length })}
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink>
              <Link to="/">
                <BreadcrumbIcon />
              </Link>
            </BreadcrumbLink>
            <BreadcrumbLink>
              <Link to="/location/countries">{t('states.breadcrumb.location')}</Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{t('states.breadcrumb.states')}</BreadcrumbPage>
          </Breadcrumb>
        }
        actions={addButton}
      />

      {isError ? (
        <div className="flex flex-col items-start gap-nano rounded-md border-hairline border-base-danger-300 bg-ctx-layout-surface p-xxs">
          <p className="text-base-danger-700 ts-paragraph-xs">{t('states.toast.loadError')}</p>
          <Button hierarchy="secondary" size="md" onClick={() => void refetch()}>
            {t('states.loadErrorRetry')}
          </Button>
        </div>
      ) : (
        <>
          <StateSearchBar
            defaultValue={filters.search}
            onSearch={search => updateFilters({ search, page: 1 })}
            onClear={() => updateFilters({ search: '', page: 1 })}
          />

          <StateTable
            data={pageData}
            isLoading={isLoading}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={searched.length}
            pageSize={STATE_PAGE_SIZE}
            onPageChange={page => updateFilters({ page })}
            emptyStateAction={addButton}
            onEdit={openEdit}
            onDelete={setDeleting}
          />
        </>
      )}

      <StateFormModal
        open={formOpen}
        state={editing}
        onOpenChange={open => {
          setFormOpen(open);
          if (!open) setEditing(null);
        }}
      />

      <DeleteStateModal
        open={deleting !== null}
        stateName={deleting?.name ?? ''}
        onConfirm={handleDeleteConfirm}
        onOpenChange={open => {
          if (!open) setDeleting(null);
        }}
      />
    </div>
  );
}
